import { PrivateUserResponse } from 'services/api/responses/responseTypes';
import {
  ExtendedSubscriptionType,
  SubscriptionType,
} from 'services/api/entities/user/subscriptionType';
import type { Nullable } from '../../types';
import type { ISubscriptionInfo } from '../stripe';
import { SubscriptionInfo } from '../stripe';
import PublicUser from './publicUser';
import { ISubscription } from './subscription';

export interface DownloadLimit {
  isWarning: boolean;
  remainingDownloads: number;
  daysToDownloadLimitReset: number;
}

const convertToSubscriptionType = (
  subscriptionType: string
): SubscriptionType => {
  if (
    subscriptionType === SubscriptionType.PERSONAL ||
    subscriptionType === ExtendedSubscriptionType.PERSONAL_DISTRIBUTION
  ) {
    return SubscriptionType.PERSONAL;
  }
  if (
    subscriptionType === SubscriptionType.PRO ||
    subscriptionType === ExtendedSubscriptionType.PRO_DISTRIBUTION
  ) {
    return SubscriptionType.PRO;
  }
  return SubscriptionType.FREE;
};

export default class PrivateUser extends PublicUser implements ISubscription {
  roles: Array<string>;

  emailConfirmed: boolean;

  isMuted: boolean;

  createdAt: Date;

  songDownloadLimit: Nullable<DownloadLimit>;

  stemsDownloadLimit: Nullable<DownloadLimit>;

  samplePackDownloadLimit: Nullable<DownloadLimit>;

  aiGenerationLimit: Nullable<DownloadLimit>;

  studioExportLimit: Nullable<DownloadLimit>;

  submitReleaseLimit: Nullable<DownloadLimit>;

  uploadAudioLimit: Nullable<DownloadLimit>;

  subscription: Nullable<ISubscriptionInfo>;

  subscriptionType: Nullable<SubscriptionType>;

  hasDistribution: boolean;

  constructor(data: PrivateUserResponse) {
    super(data);
    this.roles = data.roles;
    this.emailConfirmed = data.email_confirmed;
    this.isMuted = data.muted;
    this.createdAt = new Date(data.created_at.date);

    if (data.subscription) {
      this.subscription = new SubscriptionInfo(data.subscription);
    }

    if (data.download_limit) {
      this.songDownloadLimit = {
        isWarning: data.download_limit.is_warning,
        remainingDownloads: data.download_limit.downloads_available_today,
        daysToDownloadLimitReset: data.download_limit.days_to_limit_reset,
      };
    }

    if (data.download_limit_stems) {
      this.stemsDownloadLimit = {
        isWarning: data.download_limit_stems.is_warning,
        remainingDownloads: data.download_limit_stems.downloads_available_today,
        daysToDownloadLimitReset: data.download_limit_stems.days_to_limit_reset,
      };
    }

    if (data.download_limit_sample_packs) {
      this.samplePackDownloadLimit = {
        isWarning: data.download_limit_sample_packs.is_warning,
        remainingDownloads:
          data.download_limit_sample_packs.downloads_available_today,
        daysToDownloadLimitReset:
          data.download_limit_sample_packs.days_to_limit_reset,
      };
    }

    if (data.ai_generation_limit) {
      this.aiGenerationLimit = {
        isWarning: data.ai_generation_limit.is_warning,
        remainingDownloads: data.ai_generation_limit.downloads_available_today,
        daysToDownloadLimitReset: data.ai_generation_limit.days_to_limit_reset,
      };
    }

    if (data.studio_export_limit) {
      this.studioExportLimit = {
        isWarning: data.studio_export_limit.is_warning,
        remainingDownloads: data.studio_export_limit.downloads_available_today,
        daysToDownloadLimitReset: data.studio_export_limit.days_to_limit_reset,
      };
    }

    if (data.submit_release_limit) {
      this.submitReleaseLimit = {
        isWarning: data.submit_release_limit.is_warning,
        remainingDownloads: data.submit_release_limit.downloads_available_today,
        daysToDownloadLimitReset: data.submit_release_limit.days_to_limit_reset,
      };
    }

    if (data.upload_audio_limit) {
      this.uploadAudioLimit = {
        isWarning: data.upload_audio_limit.is_warning,
        remainingDownloads: data.upload_audio_limit.downloads_available_today,
        daysToDownloadLimitReset: data.upload_audio_limit.days_to_limit_reset,
      };
    }

    if (data.subscription_type) {
      this.subscriptionType = convertToSubscriptionType(data.subscription_type);
    }

    this.hasDistribution = data.add_ons_info.distribution_included;
  }
}
